import { PlusOutlined } from "@ant-design/icons";
import { useGetAdmins } from "@entities/admin/lib/hooks/useGetAdmins";
import { useUpdateBriefPlanning } from "@entities/brief/lib/hooks/useUpdateBriefPlanning";
import { useUpdateBriefPricing } from "@entities/brief/lib/hooks/useUpdateBriefPricing";
import { useUpdateBriefOperations } from "@entities/brief/lib/hooks/useUpdateOperations";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { AdminUsers } from "@shared/lib/types";
import {
  Avatar,
  Button,
  Card,
  CircularProgress,
  Select,
  Tag,
} from "@shared/ui";
import { Flex, List } from "antd";
import { Trash2 } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import styles from "./styles.module.scss";

type TeamType = "planning" | "pricing" | "operations";

interface AdminWithTeams {
  userId: string;
  firstName: string;
  lastName: string;
  role: string;
  teams: TeamType[];
}

export default function AssignUsersToCampaignCard({
  icon,
  title,
}: {
  icon: ReactNode;
  title: TeamType;
}) {
  const { brief, briefId, isLoadingBrief } = useBriefStore();
  const [adminSearchQuery, setAdminSearchQuery] = useState("");
  const [debouncedAdminSearchQuery] = useDebounce(adminSearchQuery, 500);
  const { admins, isFetching } = useGetAdmins(
    100,
    1,
    undefined,
    debouncedAdminSearchQuery
  );
  const adminsWithTeams = useMemo(() => {
    if (!brief) return [];

    const adminMap = new Map<string, AdminWithTeams>();

    // Process planning admins
    brief.planning?.allocatedTo?.forEach((admin) => {
      const userId = admin.userId;
      if (!adminMap.has(userId)) {
        adminMap.set(userId, {
          userId,
          firstName: admin.user?.firstName || "",
          lastName: admin.user?.lastName || "",
          role: admin.user?.role || "",
          teams: [],
        });
      }
      adminMap.get(userId)!.teams.push("planning");
    });

    // Process pricing admins
    brief.pricing?.allocatedTo?.forEach((admin) => {
      const userId = admin.userId;
      if (!adminMap.has(userId)) {
        adminMap.set(userId, {
          userId,
          firstName: admin.user?.firstName || "",
          lastName: admin.user?.lastName || "",
          role: admin.user?.role || "",
          teams: [],
        });
      }
      adminMap.get(userId)!.teams.push("pricing");
    });

    // Process operations admins
    brief.operations?.allocatedTo?.forEach((admin) => {
      const userId = admin.userId;
      if (!adminMap.has(userId)) {
        adminMap.set(userId, {
          userId,
          role: admin.user?.role || "",
          firstName: admin.user?.firstName || "",
          lastName: admin.user?.lastName || "",
          teams: [],
        });
      }
      adminMap.get(userId)!.teams.push("operations");
    });

    return Array.from(adminMap.values());
  }, [brief]);
  const {
    isUpdatedPlanning,
    isUpdatingPlanning,
    updateBriefPlanning,
    updatePlanningError,
  } = useUpdateBriefPlanning();
  const {
    isUpdatedPricing,
    isUpdatingPricing,
    updateBriefPricing,
    updatePricingError,
  } = useUpdateBriefPricing();
  const {
    isUpdatedOperations,
    isUpdatingOperations,
    updateBriefOperations,
    updateOperationsError,
  } = useUpdateBriefOperations();

  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([]);
  const [localAdmins, setLocalAdmins] = useState<AdminUsers[]>([]);
  const [actionUserId, setActionUserId] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>("");

  useEffect(() => {
    if (isUpdatedOperations || isUpdatedPricing || isUpdatedPlanning) {
      ToastService.success("Changes saved successfully.");
      setSelectedAdmins([]);
      setActionUserId(null);
      setActionStatus(null);
    } else if (
      updatePlanningError ||
      updatePricingError ||
      updateOperationsError
    ) {
      ToastService.error("Failed to save changes.");
    }
  }, [
    isUpdatedOperations,
    isUpdatedPricing,
    isUpdatedPlanning,
    updatePlanningError,
    updatePricingError,
    updateOperationsError,
  ]);

  useEffect(() => {
    if (admins && admins.data) {
      setLocalAdmins((prev) => [
        ...admins.data,
        ...prev.filter(
          (admin) => !admins.data.some((a: AdminUsers) => a._id === admin._id)
        ),
      ]);
    }
  }, [admins?.data]);

  const handleChange = (value: string) => {
    setSelectedAdmins([value]);
  };

  const getIsLoading = () => {
    if (title === "planning") return isUpdatingPlanning;
    if (title === "pricing") return isUpdatingPricing;
    if (title === "operations") return isUpdatingOperations;
    return false;
  };

  const isLoading = getIsLoading();

  const handleSaveChanges = () => {
    if (title === "planning") {
      updateBriefPlanning({
        updatedBriefPlanning: {
          briefId: briefId,
          allocatedTo: [
            ...selectedAdmins,
            ...(brief?.planning.allocatedTo?.map((item) => item.userId) || []),
          ],
        },
      });
    }
    if (title === "pricing") {
      updateBriefPricing({
        updatedBriefPricing: {
          briefId: briefId,
          allocatedTo: [
            ...selectedAdmins,
            ...(brief?.pricing.allocatedTo?.map((item) => item.userId) || []),
          ],
        },
      });
    }
    if (title === "operations") {
      updateBriefOperations({
        updatedBriefOperations: {
          briefId: briefId,
          allocatedTo: [
            ...selectedAdmins,
            ...(brief?.operations.allocatedTo?.map((item) => item.userId) ||
              []),
          ],
        },
      });
    }
    // Handle save changes logic here
  };

  const handleRemoveTeam = (userId: string) => {
    if (!briefId) return;

    if (isLoading) return;

    setActionUserId(userId);

    // Get all user IDs for the specific team, excluding the one being removed
    let remainingUserIds: string[] = [];

    if (title === "planning") {
      remainingUserIds =
        brief?.planning?.allocatedTo
          ?.filter((admin) => admin.userId !== userId)
          .map((admin) => admin.userId) || [];
      updateBriefPlanning({
        updatedBriefPlanning: {
          briefId,
          allocatedTo: remainingUserIds,
        },
      });
    } else if (title === "pricing") {
      remainingUserIds =
        brief?.pricing?.allocatedTo
          ?.filter((admin) => admin.userId !== userId)
          .map((admin) => admin.userId) || [];
      updateBriefPricing({
        updatedBriefPricing: {
          briefId,
          allocatedTo: remainingUserIds,
        },
      });
    } else if (title === "operations") {
      remainingUserIds =
        brief?.operations?.allocatedTo
          ?.filter((admin) => admin.userId !== userId)
          .map((admin) => admin.userId) || [];
      updateBriefOperations({
        updatedBriefOperations: {
          briefId,
          allocatedTo: remainingUserIds,
        },
      });
    }
  };
  const selectedUsers =
    adminsWithTeams?.filter((admin) => admin.teams.includes(title)) || [];

  return (
    <Card
      title={
        <Flex gap={5}>
          <Avatar icon={icon} size={22} shape="square" />
          <span style={{ textTransform: "capitalize" }}>{title}</span>
        </Flex>
      }
      extra={<Tag>{selectedUsers.length} users</Tag>}
    >
      {/* <Radio.Group
        buttonStyle="solid"
        value={brief?.[title]?.status}
        optionType="button"
        size={"small"}
        onChange={(e) => {
          if (title === "planning") {
            updateBriefPlanning({
              updatedBriefPlanning: {
                briefId: brief?._id,
                status: e.target.value,
              },
            });
          } else if (title === "operations") {
            updateBriefOperations({
              updatedBriefOperations: {
                briefId: brief?._id,
                status: e.target.value,
              },
            });
          } else if (title === "pricing") {
            updateBriefPricing({
              updatedBriefPricing: {
                briefId: brief?._id,
                status: e.target.value,
              },
            });
          }
          setActionStatus(e.target.value);
          setActionUserId(null);
        }}
        options={[
          {
            label: (
              <Flex gap={5} align="center" style={{ height: "fit-content" }}>
                <Clock3 size={10} />
                Pending
              </Flex>
            ),
            value: "Pending",
          },
          {
            label: (
              <Flex gap={5} align="center">
                <CircleDot size={10} />
                Active
              </Flex>
            ),
            value: "Active",
          },
          {
            label: (
              <Flex gap={5} align="center">
                Completed
              </Flex>
            ),
            value: "Closed",
          },
        ]}
      /> */}
      <List
        dataSource={selectedUsers}
        renderItem={(item) => (
          <List.Item key={item.userId}>
            <div key={item.userId} className={styles.recipientItem}>
              <div className={styles.recipientInfo}>
                <div className={styles.recipientAvatar}>
                  {item.firstName.charAt(0)}
                </div>
                <div>
                  <div className={styles.recipientName}>
                    {item.firstName} {item.lastName}
                  </div>
                  <div className={styles.recipientEmail}>{item.role}</div>
                </div>
              </div>
              <div className={styles.recipientActions}>
                <Button
                  danger
                  type="text"
                  size="small"
                  icon={<Trash2 size={16} />}
                  onClick={() => {
                    handleRemoveTeam(item.userId);
                  }}
                  loading={isLoading && actionUserId === item.userId}
                />
              </div>
            </div>
          </List.Item>
        )}
        className={styles.list}
        style={{
          maxHeight: 300,
          overflowY: "auto",
          marginBottom: 10,
        }}
      />
      <Flex gap={10} align="center">
        <Select
          placeholder="Add admin user"
          value={selectedAdmins?.[0]}
          onChange={handleChange}
          showSearch
          filterOption={false}
          onSearch={setAdminSearchQuery}
          loading={isFetching}
          notFoundContent={
            isFetching ? <CircularProgress size="small" /> : "No admins found"
          }
          className={styles.searchSelect}
          allowClear
          onClear={() => {
            setSelectedAdmins([]);
            setAdminSearchQuery("");
          }}
          options={localAdmins
            ?.filter(
              (admin) =>
                !brief?.planning.allocatedTo.some(
                  (a) => a.user._id === admin._id
                )
            )
            ?.filter(
              (admin) =>
                !brief?.pricing.allocatedTo.some(
                  (a) => a.user._id === admin._id
                )
            )
            ?.filter(
              (admin) =>
                !brief?.operations.allocatedTo.some(
                  (a) => a.user._id === admin._id
                )
            )
            .map((admin) => ({
              label: admin.firstName + " " + admin.lastName,
              value: admin._id,
            }))}
        />
        <Button
          icon={<PlusOutlined />}
          type="primary"
          loading={isLoading && !actionUserId && !actionStatus}
          disabled={selectedAdmins?.length === 0}
          onClick={() => {
            setActionUserId(null);
            setActionStatus(null);
            handleSaveChanges();
          }}
        />
      </Flex>
    </Card>
  );
}
